import React, {
    useContext,
    useMemo,
    useCallback,
    useState,
    useEffect,
    useRef,
} from 'react'
import parseColumns from '../../../../../utils/table/parseColumns'
import NoDataComponent from '../../../../elem/table/ssr/NoDataComponent'
import withConfig from '../../../../wrappers/withConfig'

import { FacilityAssignmentContext } from './DataContext'
import { DataContext } from '../../DataContext'
import TableContent from '../../../../elem/table/ssr/TableContent'
import clickSelection from '../../../../../utils/table/clickSelection'
import { useForm } from 'react-hook-form'
import deepEqual from 'deep-equal'
import Toast from '../../../../elem/Toast'
import { PanelStyleContext } from '../../../../elem/panel/PanelStyleContext'

export default withConfig(({ config }) => {
    const {
        params,
        setParams,
        loadingPossibleFacilities,
        possibleFacilityData,
        possibleFacilityMetadata,
        facilityListConfig,
        highlightedPossibleFacilities,
        setHighlightedPossibleFacilities,
        facilityAccessor,
        saveFacilityMergeData,
    } = useContext(FacilityAssignmentContext)
    const {
        setFormMethods,
        setFormDirty,
        setSubmissionState,
        submissionState,
        activePanel,
        setIsSubmitting,
        isSubmitting,
        errorState,
        setErrorState,
    } = useContext(DataContext)
    const { isExpanded } = useContext(PanelStyleContext)

    const headerRef = useRef(null)
    const [headerHeight, setHeaderHeight] = useState(0)

    const { pageCount, count, pageSize } = possibleFacilityMetadata
    const { ID_COLUMN } = config

    useEffect(() => {
        if (!headerRef.current) return

        const updateHeaderHeight = () => {
            setHeaderHeight(headerRef.current.offsetHeight || 0)
        }

        updateHeaderHeight()

        const resizeObserver = new ResizeObserver(updateHeaderHeight)
        resizeObserver.observe(headerRef.current)

        return () => {
            if (headerRef.current) {
                resizeObserver.unobserve(headerRef.current)
            }
        }
    }, [isExpanded])

    const adjustedPageCount =
        (count / pageSize) % 1 === 0 ? pageCount - 1 : pageCount

    const columns = useMemo(
        () => parseColumns(possibleFacilityData, facilityListConfig),
        [possibleFacilityData, facilityListConfig]
    )

    const defaultValues = useMemo(
        () => (submissionState ? submissionState[facilityAccessor] : []),
        [submissionState]
    )

    const formConfig = defaultValues
        ? { mode: 'onChange', defaultValues }
        : { mode: 'onChange' }
    const formMethods = useForm(formConfig)
    const { handleSubmit } = formMethods

    const onSubmit = useCallback(async () => {
        setIsSubmitting(true)
        setErrorState({})
        await saveFacilityMergeData(possibleFacilityData)
            .then(res => {
                setSubmissionState(res.submission)
                setErrorState(res.errors)
            })
            .catch(e => {
                Toast({
                    level: 'error',
                    message:
                        'Merge Submit: ' +
                        (e.message
                            ? e.message
                            : 'Unable to connect to the server. Please try again later.'),
                })
            })
            .finally(() => {
                setIsSubmitting(false)
            })
    }, [saveFacilityMergeData, setSubmissionState, possibleFacilityData])

    const selectedItems = useMemo(
        () => highlightedPossibleFacilities.map(x => x[ID_COLUMN]),
        [highlightedPossibleFacilities, ID_COLUMN]
    )
    const [lastSelection, setLastSelection] = useState(null)

    const onClick = useCallback(
        (row, isHighlighted, e, page) => {
            clickSelection(
                e,
                row,
                page,
                highlightedPossibleFacilities,
                possibleFacilityData,
                lastSelection,
                ID_COLUMN,
                setHighlightedPossibleFacilities,
                setLastSelection
            )
        },
        [highlightedPossibleFacilities, possibleFacilityData, lastSelection]
    )

    const updateParams = newParams => {
        setParams(newParams, 'possibleFacilities')
        setHighlightedPossibleFacilities([])
    }

    const renderErrors = () => {
        if (
            !Object.keys(errorState).some(key =>
                key.startsWith('uploadFacilityMerge')
            )
        ) {
            return null
        }
        return (
            <div style={{ padding: '1rem' }}>
                {Object.keys(errorState).map(x =>
                    errorState[x].map((error, index) => (
                        <div key={index} className={`has-text-danger-dark`}>
                            {error.errorString}
                        </div>
                    ))
                )}
            </div>
        )
    }

    return (
        <div className="possibleFacilitiesPositioning fullWidthPositioning">
            <div className="possibleFacilitiesWrapper">
                <div ref={headerRef}>
                    <div className="has-text-centered is-size-5 has-text-weight-semibold">
                        {`Filtered Wells`}
                    </div>
                    {renderErrors()}
                </div>
                <form
                    id="assigned-facilities"
                    className="form is-multiline"
                    onSubmit={handleSubmit(() => onSubmit())}
                >
                    {possibleFacilityData &&
                    possibleFacilityData.length === 0 &&
                    !loadingPossibleFacilities ? (
                        <div className="column is-12 has-text-centered">
                            <NoDataComponent
                                filterExpanded={false}
                                toggleFilter={() => {}}
                                message={
                                    "Apply filters to populate the grid. After at least 2 wells are added, select 1 well and set it as the primary well. All other wells' data (e.g., sample results, construction records, etc.) will be merged into the primary well."
                                }
                            />
                        </div>
                    ) : (
                        <TableContent
                            name="possibleFacilities"
                            {...{
                                columns,
                                loading: loadingPossibleFacilities,
                                params,
                                onClick,
                                setParams: updateParams,
                                resultsCount: count,
                                pageCount: adjustedPageCount,
                                data: possibleFacilityData,
                                filterExpanded: false,
                                filterFields: facilityListConfig,
                                showAppliedFilterString: false,
                                selectedItems,
                                extraStyle: {
                                    maxHeight: isExpanded
                                        ? `calc(70vh - ${headerHeight}px)`
                                        : '70vh',
                                    overflowY: isExpanded ? 'visible' : 'auto',
                                },
                                extraClass: 'facilityAssignmentTable',
                                selectedItemColumn: ID_COLUMN,
                                manualPagination: false,
                                shouldPaginate: false,
                            }}
                        />
                    )}
                </form>
            </div>
        </div>
    )
})
